import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'

import store from '@/store'
import { db } from '@/js/db'

export function resolveMaterial(slotOrValue) {
  if (!slotOrValue) return null
  const slot = typeof slotOrValue === 'string' ? { value: slotOrValue } : slotOrValue

  if (slot.materialId) {
    const fromLive = store.state.materials?.find((m) => m.id === slot.materialId)
    if (fromLive) return fromLive
    return store.state.materialsByIdCache?.[slot.materialId] || null
  }

  if (slot.value) {
    const fromLive = store.state.materials?.find((m) => m.value === slot.value)
    if (fromLive) return fromLive
    return store.state.latestArchivedByValueCache?.[slot.value] || null
  }

  return null
}

export function getSupersedingLiveMaterial(slot) {
  if (!slot || !slot.materialId) return null
  const resolved = resolveMaterial(slot)
  if (!resolved || !resolved.archived) return null
  const live = store.state.materials?.find((m) => m.value === resolved.value)
  return live && live.id !== resolved.id ? live : null
}

export function isMaterialDiscontinued(slot) {
  if (!slot || (!slot.materialId && !slot.value)) return false
  const resolved = resolveMaterial(slot)
  if (!resolved) return true
  if (!resolved.archived) return false
  return !store.state.materials?.some((m) => m.value === resolved.value)
}

async function ensureMaterialByIdLoaded(id) {
  if (!id) return
  if (store.state.materials?.some((m) => m.id === id)) return
  if (store.state.materialsByIdCache?.[id]) return
  const snap = await getDoc(doc(db, 'materials', id))
  if (!snap.exists()) return
  store.commit('cacheMaterialById', { ...snap.data(), id: snap.id })
}

async function ensureLatestArchivedByValueLoaded(value) {
  if (!value) return
  if (store.state.materials?.some((m) => m.value === value)) return
  if (store.state.latestArchivedByValueCache?.[value]) return
  const snap = await getDocs(
    query(collection(db, 'materials'), where('value', '==', value), where('archived', '==', true))
  )
  let latest = null
  snap.forEach((d) => {
    const data = d.data()
    if (!data.dateArchived) return
    if (!latest || data.dateArchived.seconds > latest.dateArchived.seconds) {
      latest = { ...data, id: d.id }
    }
  })
  if (latest) {
    store.commit('cacheLatestArchivedByValue', { value, material: latest })
  }
}

export async function prefetchMaterialsForVersion(version) {
  if (!version || !version.materials) return
  const tasks = []
  for (const slot of version.materials) {
    if (slot.materialId) {
      tasks.push(ensureMaterialByIdLoaded(slot.materialId))
    } else if (slot.value) {
      tasks.push(ensureLatestArchivedByValueLoaded(slot.value))
    }
  }
  await Promise.all(tasks)
}

export function pinMaterialIdsOnVersion(version) {
  if (!version || !version.materials) return
  for (const slot of version.materials) {
    if (slot.materialId) continue
    if (!slot.value) continue
    const resolved = resolveMaterial(slot)
    if (resolved && resolved.id) slot.materialId = resolved.id
  }
}

export async function convertOnReadForOrder(version, orderCreatedAt) {
  return convertOnReadAt(version, orderCreatedAt)
}

export async function convertOnReadForPublished(version, publishedAt) {
  return convertOnReadAt(version, publishedAt)
}

async function convertOnReadAt(version, atTimestamp) {
  if (!version || !version.materials) return false
  let dirty = false
  for (const slot of version.materials) {
    if (slot.materialId) continue
    if (!slot.value) continue
    const resolved = await resolveSlotAtTimestamp(slot.value, atTimestamp)
    if (resolved && resolved.id) {
      slot.materialId = resolved.id
      dirty = true
    }
  }
  return dirty
}

async function resolveSlotAtTimestamp(value, createdAt) {
  // 1. Validity-window match against archived docs.
  const archivedSnap = await getDocs(
    query(collection(db, 'materials'), where('value', '==', value), where('archived', '==', true))
  )
  const archivedDocs = archivedSnap.docs.map((d) => ({ ...d.data(), id: d.id }))

  const createdSeconds = createdAt && createdAt.seconds
  if (createdSeconds) {
    const match = archivedDocs.find((m) => {
      if (!m.dateArchived) return false
      const archivedSeconds = m.dateArchived.seconds
      const prevSeconds = m.prevArchivedDate ? m.prevArchivedDate.seconds : null
      // (prevArchivedDate, dateArchived] covers createdAt
      const aboveLowerBound = prevSeconds === null ? true : createdSeconds > prevSeconds
      const atOrBelowUpperBound = createdSeconds <= archivedSeconds
      return aboveLowerBound && atOrBelowUpperBound
    })
    if (match) {
      store.commit('cacheMaterialById', match)
      return match
    }
  }

  // 2. Current live doc.
  const live = store.state.materials?.find((m) => m.value === value)
  if (live) return live

  // 3. Latest archived as last resort.
  let latest = null
  for (const m of archivedDocs) {
    if (!m.dateArchived) continue
    if (!latest || m.dateArchived.seconds > latest.dateArchived.seconds) {
      latest = m
    }
  }
  if (latest) {
    store.commit('cacheMaterialById', latest)
    store.commit('cacheLatestArchivedByValue', { value, material: latest })
    return latest
  }
  return null
}
